import React, { useMemo, useState } from 'react';
import { Box, FormControl, IconButton, Link, MenuItem, Select } from '@material-ui/core';
import { FaTruck, FaWhatsapp } from 'react-icons/fa';

import DataTable from '../../../components/DataTable';
import useEnvios, { ENVIOS_FILTERS } from './useEnvios';
import OrderDetailsModal from './OrderDetailsModal';
import { toast } from 'react-toastify';
import { getUserNode } from '../../../services/auth_node';
import { producaov2 } from '../../../services/apis';
import { systemLogs } from '../../../services/system_logs';
import cleanTitle from '../../../utils/cleanTitle';
import CodeModal from './CodeModal';

const statusMapper = {
  0: 'Em aberto',
  1: 'Enviado para gráfica',
  2: 'Aguardando código de rastreio',
  3: 'Código enviado'
}

const STATUS_CODIGO_ENVIADO = 3;

const typeMapper = {
  material: 'Material',
  certificado: 'Certificado'
}

const statusOptions = Object.entries(statusMapper).map(([key, value]) => ({
  key,
  name: value
}));

const typeOptions = Object.entries(typeMapper).map(([key, value]) => ({
  key,
  name: value
}));

export function StatusSelect(props) {
  const [status, setStatus] = useState(props?.status);
  const [isLoading, setIsLoading] = useState(false);

  const handleChangeStatus = async (newStatus) => {
    // "Código enviado" só é gravado pelo modal, que exige o código de rastreio
    if (Number(newStatus) === STATUS_CODIGO_ENVIADO) {
      props?.onRequestCode();
      return;
    }

    const prevStatus = status;

    setStatus(newStatus);

    try {
      setIsLoading(true);

      const change_status = await producaov2.put(`/admin/envios/update/${props?.id}`, {
        status: newStatus
      });

      if (change_status.status === 200) {
        toast.success(`Status do pedido ${props?.orderID} atualizado com sucesso`);
        systemLogs("Alteração de Status de Envio", `O usuário ${getUserNode().id || "nao encontrado"} alterou o status do envio: ${props?.id} para: ${newStatus} `);
        return;
      }

    } catch (error) {
      toast.error(`Houve um erro ao atualizar o status do pedido ${props?.orderID}`);

      setStatus(prevStatus);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <FormControl variant="outlined" size="small" fullWidth disabled={isLoading}>
      <Select value={status} title={statusMapper[status]} onChange={e => handleChangeStatus(e.target.value)} fullWidth>
        {statusOptions.map(option => {
          return (
            <MenuItem value={option.key}>
              {option.name}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

export default function ControleEnviosNovo() {
  const {
    data,
    summary,
    isLoading,
    filters,
    page,
    rowsPerPage,
    total,
    changeFilters,
    changePage,
    changeRowsPerPage,
    clearFilters,
    refreshData
  } = useEnvios();

  const [selectedEnvio, setSelectedEnvio] = useState({
    envio: null,
    modalType: null
  });

  const resetSelectedEnvio = () => {
    setSelectedEnvio({
      envio: null,
      modalType: null
    });
  }

  const handleOpenWPMessage = ({number, name, product}) => {
    if (!number) {
      swal("Atenção", "Usuário não possui número registrado", "warning")
    };
    const formatedNumber = number
      .replace("+", "")
      .replace("-", "")
      .replace("_", "")
      .replace("(", "")
      .replace(")", "")
      .replace(" ", "");
    const link = `https://wa.me//${formatedNumber}?text=${encodeURIComponent(
      `Olá ${name.split(" ")[0]
      }! Aqui é o Lúcio, da área de Sucesso do Cliente da Voitto. Vi que você realizou a compra de material impresso do curso ${cleanTitle(product)}, porém seu cadastro não consta o endereço. Pode me passar o endereço de entrega, por gentileza?`
    )}`;

    window.open(link, "_blank");
  };

  const rows = useMemo(() => {
    return data?.map(envio => {
      return {
        id: envio?.id,
        envio,
        orderID: envio?.id_pedido,
        user: envio?.pessoa?.nome,
        userID: envio?.pessoa?.id,
        status: envio?.status_new,
        courseID: envio?.id_curso_item,
        course: envio?.curso?.curso,
        type: envio?.tipo,
        code: envio?.codigo_rastreio,
        actions: (
          <Box>
            <IconButton size="small" title="Configurar envio" onClick={() => setSelectedEnvio({ envio, modalType: 'code' })}>
              <FaTruck color="#000" size="1.25rem" />
            </IconButton>
            <IconButton size="small" title="Enviar para o Whatsapp" onClick={() => handleOpenWPMessage({ number: envio.pessoa.celular, name: envio.pessoa.nome, product: cleanTitle(envio.curso.curso || "")})}>
              <FaWhatsapp color="#000" size="1.25rem" />
            </IconButton>
          </Box>
        )
      }
    })
  }, [data]);

  const summaryRows = useMemo(() => {
    return {
      waitingCode: summary?.aguardando_codigo,
      sent: summary?.codigo_enviado,
      pending: summary?.em_aberto,
      preparation: summary?.em_preparacao
    }
  }, [summary]);

  return (
    <Box display="flex" flexDirection="column" gridRowGap="1rem">
      <DataTable
        showFilters={false}
        showPagination={false}
        isLoading={isLoading}
        rowsPerPage={10}
        rows={[summaryRows]}
        columns={[
          { key: 'pending', name: 'Em aberto' },
          { key: 'preparation', name: 'Enviado para gráfica' },
          { key: 'waitingCode', name: 'Aguardando código de rastreio' },
          { key: 'sent', name: 'Código enviado' }
        ]}
      />
      <DataTable
        showFilters
        showPagination
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        selectedFilters={filters}
        filters={[
          { key: ENVIOS_FILTERS.TYPE, name: 'Tipo', component: { type: 'select', options: [{ key: 'all', name: 'Todos' }, ...typeOptions] } },
          { key: ENVIOS_FILTERS.STATUS, name: 'Status', component: { type: 'select', options: [{ key: 'all', name: 'Todos' }, ...statusOptions] } },
          { key: ENVIOS_FILTERS.SEARCH, name: 'Pesquisa', component: { type: 'text' } }
        ]}
        columns={[
          {
            key: 'orderID',
            name: 'Pedido',
            renderCustomRow: (row) => {
              return <Link component="button" underline="always" onClick={() => setSelectedEnvio({ envio: row, modalType: 'order'})}>{row?.orderID}</Link>
            }
          },
          { key: 'user', name: 'Aluno', maxWidth: '300px', isSingleLine: true },
          {
            key: 'status',
            name: 'Status',
            maxWidth: '240px',
            renderCustomRow: (row) => {
              return (
                <StatusSelect
                  key={row?.id}
                  {...row}
                  onRequestCode={() => setSelectedEnvio({ envio: row?.envio, modalType: 'code' })}
                />
              )
            }
          },
          {
            key: 'type',
            name: 'Tipo',
            renderCustomRow: (row) => (`${typeMapper[row?.type]} - ${row?.courseID}`)
          },
          { key: 'actions', name: 'Ações' }
        ]}
        rows={rows}
        totalRecords={total}
        onPageChange={changePage}
        onRowsPerChange={changeRowsPerPage}
        onResetFilters={clearFilters}
        onSubmitFilters={changeFilters}
      />
      {selectedEnvio.envio && selectedEnvio.modalType === 'order' && (
        <OrderDetailsModal
          isOpen={!!selectedEnvio}
          selectedEnvio={selectedEnvio.envio}
          onClose={resetSelectedEnvio}
        />
      )}
      {selectedEnvio.envio && selectedEnvio.modalType === 'code' && (
        <CodeModal
          isOpen={!!selectedEnvio.envio}
          selectedEnvio={selectedEnvio.envio}
          onClose={resetSelectedEnvio}
          onSubmit={() => {
            resetSelectedEnvio();
            refreshData();
          }}
        />
      )}
    </Box>
  );
}